<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb />
      </div>
    </div>
    <b-card>
      <h4 class="mb-2 mb-xl-0 mr-2 font-weight-bold">會員等級管理</h4>
      
      <div class="row mx-0 justify-content-between mb-2 mt-3">
        <div>
          <b-button v-b-toggle="['collapse-filter', 'collapse-search']" variant="outline-info">
            <span class="mdi mdi-magnify"></span>進階搜尋
          </b-button>
        </div>
        <div>
          <b-button
            v-if="
              $permissions.has(
                $permissions.consts.CUSTOMER_LEVEL_BATCH_RECOMPUTE
              )
            "
            variant="success"
            class="flex-shrink-0 mb-2 mb-xl-0 mr-2"
            @click="recomputeLevel"
          >
            <i class="mdi mdi-flask-outline"></i>會員等級即時運算
          </b-button>
          <b-button
            v-if="$permissions.has($permissions.consts.CUSTOMER_LEVEL_CREATE)"
            variant="primary"
            class="flex-shrink-0 mb-2 mb-xl-0"
            :to="{ name: 'CustomerLevelsCreate' }"
          >
            新增會員等級
          </b-button>
        </div>
      </div>

      <b-collapse id="collapse-filter" class="mt-2">
        <b-card>
          <b-form inline>
            <b-form-group 
              label="生效狀態"
              class="mr-2 mb-2"
            >
              <b-form-select
                v-model="query.is_processing"
                :options="isProcessingOptions"
                @change="handlePageResetWithoutReload"
              ></b-form-select>
            </b-form-group>
            <b-form-group 
              label="啟用狀態"
              class="mr-2 mb-2"
            >
              <b-form-select
                v-model="query.is_enabled"
                :options="isEnableOptions"
                @change="handlePageResetWithoutReload"
              ></b-form-select>
            </b-form-group>
          </b-form>
        </b-card>
      </b-collapse>

      <b-collapse id="collapse-search" class="mt-2">
        <b-card>
          <b-input-group>
            <b-form-input
              v-model="query.keyword"
              @keyup.enter="handlePageResetWithoutReload"
              placeholder="搜尋名字、描述"
              style="max-width: 500px;"
            ></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageResetWithoutReload"
                ><i class="fa fa-search"></i
              ></b-button>
            </b-input-group-append>
          </b-input-group>
        </b-card>
      </b-collapse>
      
      <div class="row">
        <div class="col-12">
          <b-table
            striped
            hover
            responsive
            :items="levels"
            :fields="fields"
            :busy="isLoading"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle"></b-spinner>
              </div>
            </template>
            <template #cell(is_default)="data">
              <div v-if="data.item.is_default" style="text-align: center">
                <i class="mdi mdi-check"></i>
              </div>
            </template>
            <template #cell(name)="data">
              <router-link
                :to="{
                  name: 'CustomerLevelsEdit',
                  params: { id: data.item.id },
                }"
                >{{ hiddenString(data.item.name, 20) }}</router-link
              >
            </template>
            <template #cell(logo_url)="data">
              <b-img-lazy
                v-if="data.item.logo_url"
                class="mx-2 thumb-image level-image"
                :src="data.item.logo_url"
                alt="等級圖"
                height="50"
              />
            </template>
            <template #cell(card_picture_url)="data">
              <b-img-lazy
                v-if="data.item.card_picture_url"
                class="thumb-image level-image"
                :src="data.item.card_picture_url"
                alt="會員卡底圖"
                height="50"
              />
            </template>
            <template #cell(start_to_end)="data">
              <p>
                {{ convertTimeStyle(data.item.start_at) }}
                <a v-if="data.item.start_at && data.item.end_at"> / </a>
              </p>
              <p>{{ convertTimeStyle(data.item.end_at) }}</p>
            </template>
            <template #cell(is_processing)="data">
              <b-badge
                :variant="data.item.is_processing ? 'success' : 'danger'"
              >
                {{ data.item.is_processing ? "生效中" : "已失效" }}
              </b-badge>
            </template>
            <template #cell(is_enabled)="data">
              <b-badge
                :variant="
                  !!data.item.is_enabled === true ? 'success' : 'danger'
                "
                class="mr-1"
                >{{
                  !!data.item.is_enabled === true ? "啟用" : "停用"
                }}</b-badge
              >
            </template>
            <template #cell(actions)="data">
              <b-button
                v-if="checkPermission([consts.CUSTOMER_LEVEL_LIST])"
                class="ml-2"
                variant="inverse-info"
                :to="{
                  name: 'CustomerLevelsCustomerList',
                  params: { id: data.item.id },
                }"
                ><span class="mdi mdi-account-multiple"></span>
                會員列表</b-button
              >

              <b-button
                v-if="
                  checkPermissionAny([
                    consts.CUSTOMER_LEVEL_AUTO_CHANGE_VIEW,
                    consts.CUSTOMER_LEVEL_AUTO_CHANGE_EDIT,
                  ])
                "
                class="ml-2"
                variant="inverse-info"
                :to="{
                  name: 'CustomerLevelRuleList',
                  query: { level_id: data.item.id },
                }"
                ><span class="mdi mdi-settings"></span> 規則設定</b-button
              >

              <b-button
                v-if="checkPermission([consts.CUSTOMER_LEVEL_EDIT])"
                class="ml-2"
                variant="inverse-primary"
                :to="{
                  name: 'CustomerLevelsView',
                  params: { id: data.item.id },
                }"
                ><span class="mdi mdi-eye"></span> 查看</b-button
              >

              <b-button
                v-if="checkPermission([consts.CUSTOMER_LEVEL_EDIT])"
                class="ml-2"
                variant="inverse-warning"
                :to="{
                  name: 'CustomerLevelsEdit',
                  params: { id: data.item.id },
                }"
                ><span class="mdi mdi-lead-pencil"></span> 編輯</b-button
              >

              <span :id="`delete-btn-${data.item.id}`" class="d-inline-block">
                <b-button
                  v-if="checkPermission([consts.CUSTOMER_LEVEL_DELETE])"
                  class="ml-2"
                  variant="inverse-danger"
                  @click="handleDelete(data.item)"
                  :disabled="data.item.is_default || data.item.customers_count > 0"
                  ><span class="mdi mdi-delete"></span> 刪除</b-button
                >
              </span>
              <b-tooltip v-if="data.item.is_default" :target="`delete-btn-${data.item.id}`" placement="bottom">
                預設會員等級無法刪除
              </b-tooltip>
              <b-tooltip v-else-if="data.item.customers_count > 0" :target="`delete-btn-${data.item.id}`" placement="bottom">
                此等級內已存在會員，無法刪除，請整批移轉會員後再進行刪除
              </b-tooltip>
            </template>
          </b-table>
        </div>
      </div>

      <CustomPagination
        :currentPage="query.page"
        :totalRows="totalRows"
        :perPage="query.per_page"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import customerLevelApi from "@/apis/customer-levels";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import moment from "moment";
import "bootstrap-vue/dist/bootstrap-vue.css";
import { paginationMixin } from "@/mixins/pagination";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import {
  updateQueryFromRoute,
  updateUrlWithoutReload,
} from "@/utils/updateUrl";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: { CustomPagination, BreadCrumb },
  mixins: [paginationMixin],
  data() {
    return {
      consts,
      isLoading: false,
      totalRows: 0,
      isFetchCustomerList: false, // old
      customers: [], // decp
      levels: [],
      isEnableOptions: [
        { text: "選擇啟用狀態", value: null },
        { text: "已啟用", value: true },
        { text: "已停用", value: false },
      ],
      isProcessingOptions: [
        { text: "選擇生效狀態", value: null },
        { text: "生效中", value: true },
        { text: "已失效", value: false },
      ],
      fields: [
        { key: "order", label: "等級排序" },
        { key: "is_default", label: "預設等級" },
        { key: "name", label: "名稱" },
        { key: "code", label: "等級代碼" },
        { key: "logo_url", label: "等級底圖" },
        { key: "card_picture_url", label: "會員卡底圖" },
        { key: "customers_count", label: "人數" },
        { key: "start_to_end", label: "有效期間" },
        {
          key: "is_processing",
          label: "生效狀態",
          formatter: (value, key, item) => {
            const now = new Date();
            const startAt = item.start_at ? new Date(item.start_at) : null;
            const endAt = item.end_at ? new Date(item.end_at) : null;

            item.is_processing =
              (startAt === null || startAt < now) &&
              (endAt === null || endAt > now);

            return item.is_processing;
          },
        },
        { key: "is_enabled", label: "啟用狀態" },
        { key: "actions", label: "管理" },
      ],
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        is_enabled: null,
        is_processing: null,
        keyword: "",
      },
    };
  },

  computed: {
    ...mapGetters("route", ["routeQuery"]),
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
  },

  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrlWithoutReload(this.query, this.$store, this.$router);
      },
    },
    routeQuery() {
      if (!this.initialized) return;
      this.getCustomerLevelsList();
    },
  },

  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
    this.init();
  },

  mounted() {
    this.checkAndExpandAdvancedSearch();
  },

  methods: {
    checkAndExpandAdvancedSearch() {
      // 當網址有查詢參數時，自動展開進階搜尋
      const hasQueryParams = Object.keys(this.$route.query).length > 0;
      if (hasQueryParams) {
        this.$nextTick(() => {
          this.$root.$emit('bv::toggle::collapse', 'collapse-filter');
          this.$root.$emit('bv::toggle::collapse', 'collapse-search');
        });
      }
    },
    async init() {
      await this.getCustomerLevelsList();
      this.initialized = true;
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    checkPermissionAny(permissions) {
      const checker = new PermissionChecker();
      return checker.checkAny(permissions);
    },
    convertTimeStyle(date) {
      return date ? moment(date).format("YYYY-MM-DD") : "";
    },
    async getCustomerLevelsList() {
      try {
        this.isLoading = true;

        const { data } = await customerLevelApi.list({
          ...this.query,
        });

        this.levels = data.data;
        this.totalRows = data.meta.total;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    hiddenString(value, length) {
      value = value || "";
      const splittedValue = [...value];
      if (splittedValue.length <= length) {
        return value;
      }
      return splittedValue.slice(0, length).join("") + "...";
    },
    async handleDelete(level) {
      let confirm = await this.$swal.fire({
        type: "warning",
        title: "刪除會員等級",
        text: `您確認要刪除 ${level.name} 這個會員等級嗎？所有此等級會員將會降回預設等級`,
        confirmButtonText: "確認",
        cancelButtonText: "取消",
        showCancelButton: true,
      });

      if (!confirm.value) return;

      try {
        await customerLevelApi.delete(level.id);
        this.$swal("刪除成功", "", "success");
        await this.getCustomerLevelsList();
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "", "error");
      }
    },
    async recomputeLevel() {
      this.$swal.fire({
        title: "處理中",
        text: "會員等級即時運算中...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });

      try {
        await customerLevelApi.recomputeLevel();
        this.$swal.fire({
          icon: "success",
          title: "成功",
          text: "會員等級即時運算成功",
        });
        await this.getCustomerLevelsList();
      } catch (error) {
        console.error(error);
        this.$swal.fire({
          icon: "error",
          title: "失敗",
          text: "會員等級即時運算失敗",
        });
      }
    },
  },
};
</script>

<style scoped>
.level-image {
  width: 100% !important;
}
</style>
